import { getQueryValue, makeRequest } from "#common";
import { useCookieStorage } from "#storage";
import type { KnownLanguage, Market } from "#types";
import type { Copy, Options, Urls } from "./types.ts";

const preferredLanguageStorage = useCookieStorage<string>({
  key: "PREFERRED_LANGUAGE",
});

type KnownLanguages = [KnownLanguage, ...KnownLanguage[]];

export function fetchCopy<R = Copy>(
  urls: Urls<"se">,
  market: "se",
  options?: Options,
): Promise<{ copy: R; language: KnownLanguage<"se"> }>;
export function fetchCopy<R = Copy>(
  urls: Urls<"fi">,
  market: "fi",
  options?: Options,
): Promise<{ copy: R; language: KnownLanguage<"fi"> }>;
export function fetchCopy<R = Copy>(
  urls: Partial<Urls>,
  market: Market,
  options?: Options,
): Promise<{ copy: R; language: KnownLanguage }> {
  const supportedLanguages = Object.keys(urls) as unknown as KnownLanguages;
  const language = getPreferredLanguage(
    [...supportedLanguages, "debug"],
    market,
    options,
  );
  if (language === "debug") {
    return Promise.resolve({ copy: {} as R, language });
  }
  const url = urls[language];
  if (!url) {
    throw new Error(
      `Can not extract valid language url. ${language} is missing`,
    );
  }
  return makeRequest<R>({
    method: "get",
    url,
  }).then((copy) => ({
    copy,
    language,
  }));
}

/**
 * @internal
 * Cached page language.
 * This is used inorder so we can call `getPageLanguage` without searching for the `html` tag and parsing the lang attribute.
 */
let pageLanguage: KnownLanguage | null = null;

/**
 * Sets the `lang` attribute to the html-tag
 * @param language Known language code, ie. 'sv', 'en' or 'fi'
 */
export function setPageLanguage(language: KnownLanguage): void {
  pageLanguage = language;
  document.querySelector("html")?.setAttribute("lang", language);
}

/**
 * Gets the `lang` attribute from the html-tag.
 * @see setPageLanguage for setting the attribute
 */
export function getPageLanguage(): KnownLanguage | null {
  return pageLanguage;
}

export function getPreferredLanguage<SupportedLanguages extends KnownLanguages>(
  supportedLanguage: SupportedLanguages,
  market: Market,
  options: Options = {},
): SupportedLanguages[number] {
  // First check if we have a valid local in the url
  const queryLang = getLangFromParam();
  if (isLanguageSupported(supportedLanguage, queryLang)) {
    setPreferredLanguage(queryLang);
    return queryLang;
  }

  // If there is no valid local in the url, lets check local storage
  const storedLocale = getStoredLocale();
  if (isLanguageSupported(supportedLanguage, storedLocale)) {
    return storedLocale;
  }

  // See if we can get the local from the browser
  const browserLang = getBrowserLanguage(window.navigator);
  if (
    isLanguageSupported(supportedLanguage, browserLang) &&
    options.useBrowserLocale !== false
  ) {
    return browserLang;
  }

  // Use the default language
  const defaultLang = getDefaultLanguage(market);
  if (isLanguageSupported(supportedLanguage, defaultLang)) {
    return defaultLang;
  }

  console.warn(`Default language ("${defaultLang}") is not supported`);

  return supportedLanguage[0];
}

export function setPreferredLanguage(language: KnownLanguage): void {
  try {
    preferredLanguageStorage.set(language);
  } catch (error) {
    console.warn("Could not write preferred locale", error);
  }
}

function isLanguageSupported(
  supportedLanguage: KnownLanguages,
  language?: string | null,
): language is KnownLanguage {
  if (!language) {
    return false;
  }
  return (supportedLanguage as string[]).includes(language);
}

function getDefaultLanguage(market: Market): KnownLanguage {
  switch (market) {
    case "fi":
      return "en";
    case "se":
      return "sv";
    default:
      throw new Error(`Unknown market ${market}`);
  }
}

function getStoredLocale(): string | undefined {
  try {
    const value = preferredLanguageStorage.get();
    return value === "debug" ? "debug" : value?.substring(0, 2);
  } catch (error) {
    console.warn("Could not read preferred locale", error);
  }
}

function getLangFromParam(): string | undefined {
  const value = getQueryValue("locale");
  return value === "debug" ? "debug" : value?.substring(0, 2);
}

function getBrowserLanguage(navigator: {
  language?: string;
  userLanguage?: string;
  languages?: readonly string[];
}): string | undefined {
  if (navigator.languages?.[0]) {
    return navigator.languages[0]?.substring(0, 2);
  } else if (navigator.language) {
    return navigator.language.substring(0, 2);
  } else {
    return navigator.userLanguage?.substring(0, 2);
  }
}
